import {
  BarChart3,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  ClipboardCheck,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Palette,
  PlugZap,
  ScrollText,
  Settings,
  SlidersHorizontal,
  Stethoscope,
  Tags,
  UserCog,
  Users,
} from 'lucide-react';

import type { NavSection } from '@platform/ui';

import { FEEDBACK_PERMISSIONS } from '../features/feedback/types';
import { NOTIFICATION_PERMISSIONS } from '../features/notifications/types';
import { PRICING_PERMISSIONS } from '../features/pricing/types';
import { REVIEW_PERMISSIONS } from '../features/review/types';

import { ADMIN_PERMISSIONS, type Permissions } from './session/permissions';

/**
 * The clinic admin navigation (M2.7, FR-UI-05, FR-ADMIN, MASTER_PLAN M11, M4.11).
 *
 * Items are filtered by the caller's permissions from `GET /auth/session`. That is a
 * **display** decision and nothing else: every endpoint behind every item checks the
 * same key on the server, and a `clinic_staff` member reaching a link they should not
 * see would be refused there (rules §17.2, §17.3, §22.2). What the filter buys is a
 * receptionist who is not shown a menu of things their role cannot do.
 *
 * With the permission sets in `@platform/contracts`, `clinic_staff` sees Dashboard,
 * Appointments, Patients, Intake forms, Providers and Feedback; `clinic_admin`
 * additionally sees Appointment types, Scheduling, Integrations, Settings, Users and
 * roles, and Logs. A section whose every item is hidden disappears with its heading,
 * rather than leaving an empty "Clinic" label.
 *
 * **Dashboard is always visible.** It is where `?next=` falls back to and where a
 * sign-in lands, so hiding it would mean a successful sign-in with nowhere to go; the
 * screen itself is behind `appointments.read` on the server and says so when it is
 * refused. Everything else is gated.
 *
 * **M11.4/M11.5/M11.8/M11.9/M11.10 added four items**: Settings, Users and roles, and
 * Logs under the Clinic heading, and Feedback under Platform. **M11.7 added Reports**,
 * which closes `Docs/ADMIN_AUTH_UI.md`'s open item 4: every item on that list now has a
 * route. It is the one item gated by *any of* four keys rather than one, because a role
 * may hold any subset of the `reports.*` set.
 *
 * It lives here rather than in `__root.tsx` so a test can assert the shape without
 * mounting a router.
 */
export function navigation(pathname: string, can: Permissions['can']): NavSection[] {
  const primary = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      href: '/',
      active: pathname === '/',
      icon: <LayoutDashboard size={18} strokeWidth={2} />,
      visible: true,
    },
    {
      key: 'appointments',
      label: 'Appointments',
      href: '/appointments',
      active: pathname.startsWith('/appointments'),
      icon: <CalendarDays size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.appointmentsRead),
    },
    {
      key: 'patients',
      label: 'Patients',
      href: '/patients',
      active: pathname.startsWith('/patients'),
      icon: <Users size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.patientsRead),
    },
    {
      key: 'forms',
      label: 'Intake forms',
      href: '/forms',
      active: pathname.startsWith('/forms'),
      icon: <ClipboardList size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.formsRead),
    },
  ];

  /* Everything that configures the clinic rather than running it (M4.11). */
  const clinic = [
    {
      key: 'providers',
      label: 'Providers',
      href: '/providers',
      active: pathname.startsWith('/providers'),
      icon: <Stethoscope size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.providersRead),
    },
    {
      key: 'appointment-types',
      label: 'Appointment types',
      href: '/appointment-types',
      active: pathname.startsWith('/appointment-types'),
      icon: <CalendarClock size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.appointmentTypesManage),
    },
    {
      /* The screen edits working hours *and* booking settings, so either key opens it
         and the screen's own sections are gated by the server. */
      key: 'scheduling',
      label: 'Scheduling',
      href: '/scheduling',
      active: pathname.startsWith('/scheduling'),
      icon: <SlidersHorizontal size={18} strokeWidth={2} />,
      visible:
        can(ADMIN_PERMISSIONS.schedulesManage) || can(ADMIN_PERMISSIONS.bookingSettingsManage),
    },
    {
      /*
       * M6.11. `pricing.read` opens the editor; publishing additionally needs
       * `pricing.manage` on the server, and the screen renders read-only without it
       * rather than hiding the item — a receptionist asked "what do we charge?" should
       * be able to answer it.
       */
      key: 'pricing',
      label: 'Pricing',
      href: '/pricing',
      active: pathname.startsWith('/pricing'),
      icon: <Tags size={18} strokeWidth={2} />,
      visible: can(PRICING_PERMISSIONS.read),
    },
    {
      /* M6.9 UI. One key covers the queue and every action on it. */
      key: 'review',
      label: 'Review queue',
      href: '/review',
      active: pathname.startsWith('/review'),
      icon: <ClipboardCheck size={18} strokeWidth={2} />,
      visible: can(REVIEW_PERMISSIONS.review),
    },
    {
      /*
       * M9.8. `notifications.read` opens all three screens and `clinic_staff` holds it:
       * "did the confirmation reach them?" is a front-desk question. Resending and
       * publishing a template are separate keys the screens and the server both check,
       * so the item is not gated on the administrator's half (rules §17.2, §22.2).
       */
      key: 'notifications',
      label: 'Notifications',
      href: '/notifications',
      active: pathname.startsWith('/notifications'),
      icon: <Mail size={18} strokeWidth={2} />,
      visible: can(NOTIFICATION_PERMISSIONS.read),
    },
    {
      key: 'integrations',
      label: 'Integrations',
      href: '/integrations',
      active: pathname.startsWith('/integrations'),
      icon: <PlugZap size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.integrationsRead),
    },
    {
      /* M11.4/M11.5. `/settings/domains` lives under the same item, so `startsWith`
         keeps the parent highlighted while the child screen is open. */
      key: 'settings',
      label: 'Settings',
      href: '/settings',
      active: pathname.startsWith('/settings'),
      icon: <Settings size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.settingsManage),
    },
    {
      key: 'users',
      label: 'Users and roles',
      href: '/users',
      active: pathname.startsWith('/users'),
      icon: <UserCog size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.usersRead),
    },
    {
      /*
       * M11.7. Four separate keys, any one of which is worth showing the item for: the
       * hub renders whichever reports the role holds and locks the rest, so a member of
       * staff with only `reports.communications` still has somewhere to go. Asking for
       * all four would hide the hub from somebody who can open three of the reports
       * (`features/reports/reports-hub.tsx` explains the locked cards).
       */
      key: 'reports',
      label: 'Reports',
      href: '/reports',
      active: pathname.startsWith('/reports'),
      icon: <BarChart3 size={18} strokeWidth={2} />,
      visible:
        can(ADMIN_PERMISSIONS.reportsFunnel) ||
        can(ADMIN_PERMISSIONS.reportsFinance) ||
        can(ADMIN_PERMISSIONS.reportsCommunications) ||
        can(ADMIN_PERMISSIONS.reportsIntegrations),
    },
    {
      /* `logs.read` opens the screen; the audit tab behind it additionally needs
         `audit.read` on the server. `clinic_admin` holds both (SECURITY.md §5.1). */
      key: 'logs',
      label: 'Logs',
      href: '/logs',
      active: pathname.startsWith('/logs'),
      icon: <ScrollText size={18} strokeWidth={2} />,
      visible: can(ADMIN_PERMISSIONS.logsRead) || can(ADMIN_PERMISSIONS.auditRead),
    },
  ];

  /* The design reference is a build artefact, not clinic data: no permission gates it
     and none should, because it reads nothing. */
  const platform = [
    {
      /* M11.10. `feedback.submit` is held by every role including `clinic_staff`, so this
         is the one item below the Clinic heading a receptionist also sees. It is also the
         reachable entry point for rules §50.2 until the account menu grows one - that menu
         belongs to the authentication workstream (Docs/ADMIN_SETTINGS_UI.md open item 7). */
      key: 'feedback',
      label: 'Feedback',
      href: '/feedback',
      active: pathname.startsWith('/feedback'),
      icon: <MessageSquare size={18} strokeWidth={2} />,
      visible: can(FEEDBACK_PERMISSIONS.submit),
    },
    {
      key: 'design',
      label: 'Design reference',
      href: '/design',
      active: pathname.startsWith('/design'),
      icon: <Palette size={18} strokeWidth={2} />,
      visible: true,
    },
  ];

  const strip = ({ visible: _visible, ...item }: (typeof primary)[number]) => item;

  const sections: NavSection[] = [{ items: primary.filter((item) => item.visible).map(strip) }];

  const clinicItems = clinic.filter((item) => item.visible).map(strip);
  if (clinicItems.length > 0) sections.push({ label: 'Clinic', items: clinicItems });

  const platformItems = platform.filter((item) => item.visible).map(strip);
  if (platformItems.length > 0) sections.push({ label: 'Platform', items: platformItems });

  return sections;
}

/**
 * The page title for a path (M13.7, WCAG 2.4.2 Page Titled).
 *
 * The nav already names every top-level screen, but the title has to answer for the
 * routes the menu does not list — detail records, the sub-pages behind Reports and
 * Notifications, and the two screens that render outside the shell. Longest prefix wins,
 * so `/reports/funnel` beats `/reports` and `/appointments/:id` inherits "Appointment".
 *
 * It lives beside `navigation()` because it is the same list of names, and a title that
 * disagrees with the menu item the user clicked is worse than no title at all.
 */
const TITLES: [prefix: string, title: string][] = [
  ['/appointments', 'Appointments'],
  ['/patients', 'Patients'],
  ['/forms', 'Intake forms'],
  ['/providers', 'Providers'],
  ['/appointment-types', 'Appointment types'],
  ['/scheduling', 'Scheduling'],
  ['/pricing/eligibility', 'Scheduling rules'],
  ['/pricing', 'Pricing'],
  ['/review', 'Review queue'],
  ['/notifications/templates', 'Message templates'],
  ['/notifications/reminders', 'Scheduled reminders'],
  ['/notifications', 'Notifications'],
  ['/integrations', 'Integrations'],
  ['/settings/domains', 'Domains'],
  ['/settings', 'Settings'],
  ['/users', 'Users and roles'],
  ['/reports/funnel', 'Bookings and funnel'],
  ['/reports/insurance-payments', 'Insurance and payments'],
  ['/reports/communications', 'Communications'],
  ['/reports/integrations', 'Integration health'],
  ['/reports', 'Reports'],
  ['/logs', 'Logs'],
  ['/feedback', 'Feedback'],
  ['/design', 'Design reference'],
  ['/account/security', 'Your security'],
  ['/mfa/enroll', 'Set up two-factor authentication'],
  ['/login', 'Sign in'],
];

/** `"Patients · HeyAllergy Clinic Admin"`. The suffix is the product, never the tenant. */
export function pageTitle(pathname: string): string {
  const match = TITLES.filter(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
    .sort((a, b) => b[0].length - a[0].length)
    .at(0);
  return `${match?.[1] ?? 'Dashboard'} · HeyAllergy Clinic Admin`;
}
